import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../../shared/components/core-ui/modal/modal.component';
import styles from './furnishing-requests.module.scss';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Select from 'react-select';
import { PUBLIC_PATH } from '../../../shared/config/routes-config';
import { Pagination } from '../../../shared/components/core-ui/pagination/pagination.component';
import {
  getAllFurnishingRequests,
  updateFurnishingRequest,
  FurnishingRequestResponse,
  FurnishingRequestStatus,
  getFurnishingNumberOfRooms,
  getHomeTypes,
  getAllModels,
  getAllFurnishingPackages,
  getFurnishingRequestStatusEvents,
  FurnishingRequestStatusEventsApiResponse,
  FurnishingRequestStatusEvent,
  MODULE_NAME,
  getAllAddOns
} from '../../furnishing.service';

import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { getAllAreas } from '../../../shared/services/data/lookups.data';
import api from '../../../shared/services/utils/api/api.service';
import { UserType } from '../../../shared/enums/user-type.enum';
import { connect } from 'react-redux';
import { RootState } from '../../../shared/redux/reducers';
import { UserRole } from '../../../shared/enums/user-role.enum';
import { InfoIcon } from '../../../ui/Icons';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { NotFoundData } from '../../../ui/NotFoundData';
import Button from '../../../shared/components/core-ui/button/button.component';

import { FurnishingRequestFormModal } from './components/furnishing-request-form-modal/furnishing-request-form-modal.component';

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
      fontSize: '1.4rem'
    },
    '&:hover': {
      backgroundColor: '#f5f7fc'
    }
  }
});

type RequestStatus = FurnishingRequestStatus;
type AssigneeOption = { id: string; name: string; email: string };

const statusLabelMap: Record<RequestStatus, string> = {
  new: 'New',
  ready: 'Ready',
  in_progress: 'In Process',
  won: 'Won',
  lost: 'Lost',
  complete: 'Complete'
};

export enum FurnishingRequestSource {
  WEBSITE = 'Website',
  WHATSAPP = 'WhatsApp',
  CALL_CENTER = 'CallCenter',
  INSTAGRAM = 'Instagram',
  MESSENGER = 'Messenger',
  REFERRAL = 'Referral'
}

const mapIdToLabel = (
  lookup: Record<string, string>,
  rawId: string | number | undefined | null,
  fallback: string
) => {
  const key = rawId !== undefined && rawId !== null ? String(rawId) : '';
  return (key && lookup[key]) || fallback;
};

export interface FurnishingRequestComment {
  text: string;
  author?: string;
  createdAt?: string;
}

export interface FurnishingRequest {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  source?: string;
  typeOfUnit: string;
  area: string;
  package: string;
  model: string;
  ids?: {
    typeOfUnit?: string;
    unitTypeGroup?: string | number;
    areaName?: string;
    packageId?: string;
    modelId?: string;
  };
  addOns: string[];
  comments: FurnishingRequestComment[];
  status: RequestStatus;
  assignedToId?: string;
  assignedToEmail?: string;
  assignedToSalesId?: string;
  assignedToSalesEmail?: string;
  createdAt: string;
  unitTypeGroup: string;
  price: string;
}

interface RowProps {
  request: FurnishingRequest;
  onStatusChange: (id: string, status: RequestStatus) => void;
  onEditRequest: (request: FurnishingRequest) => void;
  onDesignAssigneeChange: (id: string, value: string) => void;
  onSalesAssigneeChange: (id: string, value: string) => void;
  onCommentsChange: (
    id: string,
    comment: string,
    existingComments: FurnishingRequestComment[]
  ) => void;
  assigneeDesignOptions: AssigneeOption[];
  assigneeSalesOptions: AssigneeOption[];
  canAssignDesignAssignee: boolean;
  canAssignSalesAssignee: boolean;
  canEditRequestDetails: boolean;
}

const Row: React.FC<
  RowProps & {
    isDesignTeamUser: boolean;
    isAdminUser: boolean;
    isSalesUser: boolean;
  }
> = ({
  request,
  onStatusChange,
  onEditRequest,
  onDesignAssigneeChange,
  onSalesAssigneeChange,
  onCommentsChange,
  assigneeDesignOptions,
  assigneeSalesOptions,
  canAssignDesignAssignee,
  canAssignSalesAssignee,
  canEditRequestDetails,
  isDesignTeamUser,
  isAdminUser,
  isSalesUser
}) => {
  const classes = useRowStyles();
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [designAssigneeMenuAnchor, setDesignAssigneeMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [salesAssigneeMenuAnchor, setSalesAssigneeMenuAnchor] = useState<null | HTMLElement>(null);
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [commentInput, setCommentInput] = useState('');

  const [eventsModalOpen, setEventsModalOpen] = useState(false);
  const [statusEvents, setStatusEvents] = useState<FurnishingRequestStatusEvent[]>([]);

  const designAssigneeLabel =
    assigneeDesignOptions.find((opt: AssigneeOption) => opt.id === request.assignedToId)?.name ||
    'Assign design';

  const salesAssigneeLabel =
    assigneeSalesOptions.find((opt: AssigneeOption) => opt.id === request.assignedToSalesId)
      ?.name || 'Assign sales';

  const canEditThisRequest =
    !!canEditRequestDetails &&
    (request.source !== FurnishingRequestSource.WEBSITE || !request.source);

  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (
      (request.status === 'ready' || request.status === 'won' || request.status === 'lost') &&
      isSalesUser
    ) {
      return;
    } else if ((request.status === 'new' || request.status === 'in_progress') && isSalesUser) {
      setStatusMenuAnchor(event.currentTarget);
    } else if ((request.status === 'new' || request.status === 'in_progress') && isDesignTeamUser) {
      return;
    } else {
      setStatusMenuAnchor(event.currentTarget);
    }
  };

  const statusMenuOptions: RequestStatus[] = isAdminUser
    ? (['new', 'ready', 'in_progress', 'won', 'lost', 'complete'] as RequestStatus[])
    : isDesignTeamUser
    ? (['new', 'ready', 'in_progress', 'complete'] as RequestStatus[])
    : isSalesUser
    ? (['new', 'ready', 'won', 'lost'] as RequestStatus[])
    : (['new', 'ready'] as RequestStatus[]);

  const handleCommentsModalOpen = () => {
    setCommentsModalOpen(true);
  };

  const handleCommentsModalClose = () => {
    setCommentsModalOpen(false);
    setCommentInput('');
  };

  const fetchStatusEvents = async () => {
    const promise = getFurnishingRequestStatusEvents(request.id).then((response) => {
      if (isErrorInfo(response)) {
        setStatusEvents([]);
        throw response;
      }
      setEventsModalOpen(true);
      setStatusEvents(Array.isArray(response) ? response : []);
    });

    try {
      birdToast.promise(promise, {
        loadingMessage: 'Loading status events',
        successMessage: 'Status events loaded',
        errorFailedMessage: 'Failed to load status events',
        errorPermissionMessage: 'You do not have permission to view status events'
      });
    } catch (error) {
      console.error('Error fetching furnishing request status events:', error);
      setStatusEvents([]);
    }
  };

  const handleEventsModalOpen = async () => {
    await fetchStatusEvents();
  };

  const handleEventsModalClose = () => {
    setEventsModalOpen(false);
    setStatusEvents([]);
  };

  const handleAddComment = () => {
    const trimmedComment = commentInput.trim();
    if (!trimmedComment) {
      return;
    }
    onCommentsChange(request.id, trimmedComment, request.comments || []);

    setCommentInput('');
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
  };

  const handleStatusSelect = (status: RequestStatus) => {
    onStatusChange(request.id, status);
    handleStatusMenuClose();
  };

  const handleDesignAssigneeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDesignAssigneeMenuAnchor(event.currentTarget);
  };

  const handleDesignAssigneeMenuClose = () => {
    setDesignAssigneeMenuAnchor(null);
  };

  const handleDesignAssigneeSelect = (userId: string) => {
    onDesignAssigneeChange(request.id, userId);
    handleDesignAssigneeMenuClose();
  };

  const handleSalesAssigneeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSalesAssigneeMenuAnchor(event.currentTarget);
  };

  const handleSalesAssigneeMenuClose = () => {
    setSalesAssigneeMenuAnchor(null);
  };

  const handleSalesAssigneeSelect = (userId: string) => {
    onSalesAssigneeChange(request.id, userId);
    handleSalesAssigneeMenuClose();
  };

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell>
          <div className={styles['furnishing-requests__inline-controls']}>
            <button
              type="button"
              onClick={handleStatusMenuOpen}
              className={`${styles['furnishing-requests__status-trigger']} ${
                styles['status-badge']
              } ${styles[`status-badge--${request.status}`]}`}
            >
              <span>{statusLabelMap[request.status]}</span>

              {request.status === 'new' && <span className={styles['status-badge__icon']}>●</span>}
              {request.status === 'ready' && (
                <CheckCircleOutlineIcon className={styles['status-badge__icon']} fontSize="small" />
              )}
              {request.status === 'in_progress' && (
                <span className={styles['status-badge__loader']} />
              )}
              {request.status === 'won' && (
                <CheckCircleOutlineIcon className={styles['status-badge__icon']} fontSize="small" />
              )}
              {request.status === 'lost' && (
                <CancelOutlinedIcon className={styles['status-badge__icon']} fontSize="small" />
              )}
              {request.status === 'complete' && (
                <DoneAllIcon className={styles['status-badge__icon']} fontSize="small" />
              )}
              <ExpandMoreIcon fontSize="small" />
            </button>
            <Menu
              aria-label="status-menu"
              anchorEl={statusMenuAnchor}
              keepMounted
              open={Boolean(statusMenuAnchor)}
              onClose={handleStatusMenuClose}
            >
              {statusMenuOptions.map((status) => (
                <MenuItem
                  key={status}
                  selected={status === request.status}
                  onClick={() => handleStatusSelect(status)}
                >
                  <div className={`${styles['status-badge']} ${styles[`status-badge--${status}`]}`}>
                    <span>{statusLabelMap[status]}</span>
                    {status === 'new' && <span className={styles['status-badge__icon']}>●</span>}
                    {status === 'ready' && (
                      <CheckCircleOutlineIcon
                        className={styles['status-badge__icon']}
                        fontSize="small"
                      />
                    )}

                    {status === 'in_progress' && isDesignTeamUser && (
                      <span className={styles['status-badge__loader']} />
                    )}
                    {status === 'won' && (isSalesUser || isAdminUser) && (
                      <CheckCircleOutlineIcon
                        className={styles['status-badge__icon']}
                        fontSize="small"
                      />
                    )}
                    {status === 'lost' && (isSalesUser || isAdminUser) && (
                      <CancelOutlinedIcon
                        className={styles['status-badge__icon']}
                        fontSize="small"
                      />
                    )}
                    {status === 'complete' && (isDesignTeamUser || isAdminUser) && (
                      <DoneAllIcon className={styles['status-badge__icon']} fontSize="small" />
                    )}
                  </div>
                </MenuItem>
              ))}
            </Menu>
            <button
              type="button"
              onClick={handleCommentsModalOpen}
              className={styles['furnishing-requests__comments-trigger']}
              title="View comments"
            >
              <ChatBubbleOutlineIcon fontSize="small" />
              <span>{request.comments?.length || 0}</span>
            </button>
            <button type="button" onClick={handleEventsModalOpen} title="View events">
              <InfoIcon />
            </button>
          </div>
        </TableCell>
        <TableCell>
          <button
            type="button"
            onClick={() => onEditRequest(request)}
            disabled={!canEditThisRequest}
            className={styles['furnishing-requests__comments-trigger']}
            title={
              canEditThisRequest
                ? 'Edit request'
                : 'Edit is available only for non-Website requests'
            }
          >
            Edit
          </button>
        </TableCell>
        <TableCell>{request.fullName}</TableCell>
        {!isDesignTeamUser && <TableCell>{request.email}</TableCell>}
        <TableCell>{request.price}</TableCell>
        {!isDesignTeamUser && <TableCell>{request.phoneNumber}</TableCell>}
        <TableCell>{request.typeOfUnit}</TableCell>
        <TableCell>{request.unitTypeGroup}</TableCell>
        <TableCell>{request.package}</TableCell>
        <TableCell>{request.model}</TableCell>
        <TableCell>{request.area}</TableCell>
        <TableCell className={styles['addons-table-cell']}>
          <div className={styles['addons-cell']}>
            {request.addOns.length > 0
              ? request.addOns
                  .join(',')
                  .split(',')
                  .map((addon) => addon.trim())
                  .filter((addon) => addon.length > 0)
                  .map((addon) => (
                    <span key={addon} className={styles['addon-badge']} title={addon}>
                      {addon}
                    </span>
                  ))
              : '-'}
          </div>
        </TableCell>

        {canAssignDesignAssignee && (
          <TableCell>
            <button
              type="button"
              disabled={request.status === 'new' && isDesignTeamUser}
              onClick={handleDesignAssigneeMenuOpen}
              className={styles['furnishing-requests__assignee-trigger']}
            >
              <span>{designAssigneeLabel}</span>
              <ExpandMoreIcon fontSize="small" />
            </button>
            <Menu
              aria-label="design-assignee-menu"
              anchorEl={designAssigneeMenuAnchor}
              keepMounted
              open={Boolean(designAssigneeMenuAnchor)}
              onClose={handleDesignAssigneeMenuClose}
            >
              {assigneeDesignOptions.map((user: AssigneeOption) => (
                <MenuItem
                  key={user.id}
                  selected={user.id === request.assignedToId}
                  onClick={() => handleDesignAssigneeSelect(user.id)}
                >
                  {user.name}
                </MenuItem>
              ))}
            </Menu>
          </TableCell>
        )}

        {!canAssignDesignAssignee && isSalesUser && (
          <TableCell>
            <span className={styles['furnishing-requests__assignee-text']}>
              {designAssigneeLabel}
            </span>
          </TableCell>
        )}

        <TableCell>
          <button
            type="button"
            onClick={handleSalesAssigneeMenuOpen}
            disabled={!canAssignSalesAssignee}
            className={styles['furnishing-requests__assignee-trigger']}
          >
            <span>{salesAssigneeLabel}</span>
            <ExpandMoreIcon fontSize="small" />
          </button>
          <Menu
            aria-label="sales-assignee-menu"
            anchorEl={salesAssigneeMenuAnchor}
            keepMounted
            open={Boolean(salesAssigneeMenuAnchor)}
            onClose={handleSalesAssigneeMenuClose}
          >
            {assigneeSalesOptions.map((user: AssigneeOption) => (
              <MenuItem
                key={user.id}
                selected={user.id === request.assignedToSalesId}
                onClick={() => handleSalesAssigneeSelect(user.id)}
              >
                {user.name}
              </MenuItem>
            ))}
          </Menu>
        </TableCell>

        <TableCell>{request.createdAt}</TableCell>
      </TableRow>
      <Modal
        config={{
          showModal: commentsModalOpen,
          fullScreen: false,
          maxWidth: 'sm',
          showClose: true
        }}
        eventHandlers={{
          closeModalHandler: handleCommentsModalClose
        }}
      >
        <div className="p-8 min-w-[400px]">
          <h2 className="m-0 mb-4 text-4xl font-gilroy-medium text-neutral-500 font-semibold">
            Comments
          </h2>
          <div className={styles['comments-list']}>
            {request.comments?.length ? (
              request.comments.map((comment, index) => (
                <div key={`${request.id}-comment-${index}`} className={styles['comment-item']}>
                  <div className={styles['comment-item__meta']}>
                    <span>{comment.author || 'Unknown'}</span>
                    <span>
                      {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : '-'}
                    </span>
                  </div>
                  <div className={styles['comment-item__text']}>{comment.text}</div>
                </div>
              ))
            ) : (
              <p className={styles['comment-empty']}>No comments yet.</p>
            )}
          </div>
          <div className={styles['comments-input-row']}>
            <input
              type="text"
              value={commentInput}
              onChange={(event) => setCommentInput(event.target.value)}
              placeholder="Add a comment"
              className={styles['comments-input']}
            />
            <button
              type="button"
              onClick={handleAddComment}
              disabled={!commentInput.trim()}
              className={styles['comments-add']}
            >
              Add
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        config={{
          showModal: eventsModalOpen,
          fullScreen: false,
          maxWidth: 'sm',
          showClose: true
        }}
        eventHandlers={{
          closeModalHandler: handleEventsModalClose
        }}
      >
        <div className="p-10 min-w-[500px]">
          <h2 className="m-0 mb-8 text-4xl font-gilroy-medium text-neutral-700 font-bold border-b pb-4">
            Status Timeline
          </h2>

          {statusEvents.length === 0 && (
            <div className="py-8 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
              <p className="m-0 text-2xl text-neutral-500">No events recorded yet.</p>
            </div>
          )}

          {statusEvents.length > 0 && (
            <div className="relative pl-2 py-2">
              {/* Timeline vertical line */}
              <div className="absolute left-[9px] top-3 bottom-3 w-0.5 bg-gray-200" />

              <div className="flex flex-col gap-8">
                {statusEvents.map((eventItem) => (
                  <div key={eventItem.id} className="relative pl-10">
                    {/* Timeline Dot */}
                    <div className="absolute left-0 top-1.5 w-[20px] h-[20px] rounded-full bg-white border-[4px] border-[#237c58] z-10 shadow-sm" />

                    <div className="flex flex-col">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                        <span className="text-2xl font-bold text-neutral-800">
                          {statusLabelMap[eventItem.to]}
                        </span>
                        <span className="text-lg text-neutral-500 bg-gray-100 px-2 py-0.5 rounded">
                          from {statusLabelMap[eventItem.from]}
                        </span>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-semibold text-neutral-600">By:</span>
                          <span className="text-lg text-neutral-700">
                            {eventItem.changedByEmail || '-'}
                          </span>
                        </div>
                        <div className="text-base text-neutral-400">
                          {eventItem.createdAt
                            ? new Date(eventItem.createdAt).toLocaleString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'ready', label: 'Ready' },
  { value: 'in_progress', label: 'In Process' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'complete', label: 'Complete' }
];

export const FurnishingRequestsContainer: React.FC = ({ userRole, userRoles, userEmail }: any) => {
  const [requests, setRequests] = useState<FurnishingRequest[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    confirmText: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: () => {
      return;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [modelNameMap, setModelNameMap] = useState<Record<string, string>>({});
  const [packageNameMap, setPackageNameMap] = useState<Record<string, string>>({});
  const [homeTypeMap, setHomeTypeMap] = useState<Record<string, string>>({});
  const [unitTypeGroupMap, setUnitTypeGroupMap] = useState<Record<string, string>>({});
  const [areaNameMap, setAreaNameMap] = useState<Record<string, string>>({});
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [assigneeDesignOptions, setAssigneeDesignOptions] = useState<AssigneeOption[]>([]);
  const [assigneeSalesOptions, setAssigneeSalesOptions] = useState<AssigneeOption[]>([]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<FurnishingRequest | null>(null);
  const [createModalLookups, setCreateModalLookups] = useState({
    unitTypeOptions: [] as { label: string; value: string }[],
    roomsOptions: [] as { label: string; value: string }[],
    areaOptions: [] as { label: string; value: string }[],
    packageOptions: [] as { label: string; value: string }[],
    modelOptions: [] as { label: string; value: string }[],
    addonsOptions: [] as { label: string; value: string }[]
  });

  const isPermited = (permittedList: UserRole[]) => {
    return (
      permittedList.indexOf(userRole) > -1 ||
      permittedList.some((item) => userRoles?.includes(item))
    );
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    confirmText: string = 'Confirm'
  ) => {
    setConfirmDialog({ show: true, title, message, onConfirm, confirmText });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog({
      show: false,
      title: '',
      message: '',
      confirmText: 'Confirm',
      onConfirm: () => {
        return;
      }
    });
  };

  const handleConfirm = () => {
    confirmDialog.onConfirm();
    hideConfirmDialog();
  };

  const loadDesignTeamUsers = async () => {
    try {
      const response = await api.searchUser({
        role: [UserType.DESIGN_TEAM, UserType.SALES_SPECIALIST]
      });
      if (!isErrorInfo(response.data)) {
        const users = response.data?.data || [];
        const designTeamUsers = users.filter(
          (user) =>
            !!user.role && [UserRole.DESIGN_TEAM, UserRole.DESIGN_MANAGER].includes(user.role)
        );
        const salesSpecialistUsers = users.filter(
          (user) => user.role === UserRole.SALES_SPECIALIST
        );
        const mappedDesignTeamUsers: AssigneeOption[] = designTeamUsers.map((user) => ({
          id: user.id || '',
          name: user.name || user.email || 'Unknown',
          email: user.email || ''
        }));
        const mappedSalesUsers: AssigneeOption[] = salesSpecialistUsers.map((user) => ({
          id: user.id || '',
          name: user.name || user.email || 'Unknown',
          email: user.email || ''
        }));
        setAssigneeDesignOptions(mappedDesignTeamUsers);
        setAssigneeSalesOptions(mappedSalesUsers);
      }
    } catch (error) {
      console.error('Error loading design team users:', error);
    }
  };

  const loadLookups = async () => {
    try {
      const [packagesRes, modelsRes, homeTypesRes, unitTypesRes, areasRes, addOnsRes] =
        await Promise.all([
          getAllFurnishingPackages(),
          getAllModels(),
          getHomeTypes(),
          getFurnishingNumberOfRooms(),
          getAllAreas(MODULE_NAME),
          getAllAddOns()
        ]);

      if (!isErrorInfo(packagesRes)) {
        const mappedPackages = (packagesRes || []).reduce((acc, pkg) => {
          const key = (pkg as any)?.id;
          if (key) acc[String(key)] = (pkg as any).name;
          return acc;
        }, {} as Record<string, string>);
        setPackageNameMap(mappedPackages);

        setCreateModalLookups((prev) => ({
          ...prev,
          packageOptions: (packagesRes || [])
            .map((pkg: any) => ({
              value: String(pkg?.id || ''),
              label: String(pkg?.name || '')
            }))
            .filter((opt) => opt.value && opt.label)
        }));
      }

      if (!isErrorInfo(modelsRes)) {
        const mappedModels = (modelsRes || []).reduce((acc, model) => {
          const key = (model as any)?.id;
          if (key) acc[String(key)] = (model as any).name;
          return acc;
        }, {} as Record<string, string>);
        setModelNameMap(mappedModels);

        setCreateModalLookups((prev) => ({
          ...prev,
          modelOptions: (modelsRes || [])
            .map((model: any) => ({
              value: String(model?.id || ''),
              label: String(model?.name || '')
            }))
            .filter((opt) => opt.value && opt.label)
        }));
      }

      if (!isErrorInfo(homeTypesRes)) {
        const mappedHomeTypes = (homeTypesRes || []).reduce((acc, type) => {
          const key = (type as any)?.value;
          if (key) acc[String(key)] = (type as any).label;
          return acc;
        }, {} as Record<string, string>);
        setHomeTypeMap(mappedHomeTypes);

        setCreateModalLookups((prev) => ({
          ...prev,
          unitTypeOptions: (homeTypesRes || [])
            .map((type: any) => ({
              value: String(type?.value || ''),
              label: String(type?.label || '')
            }))
            .filter((opt) => opt.value && opt.label)
        }));
      }

      if (!isErrorInfo(unitTypesRes)) {
        const mappedUnitTypes = (unitTypesRes || []).reduce((acc, type) => {
          const key = (type as any)?.value;
          if (key) acc[String(key)] = (type as any).label;
          return acc;
        }, {} as Record<string, string>);
        setUnitTypeGroupMap(mappedUnitTypes);

        setCreateModalLookups((prev) => ({
          ...prev,
          roomsOptions: (unitTypesRes || [])
            .map((type: any) => ({
              value: String(type?.value || ''),
              label: String(type?.label || '')
            }))
            .filter((opt) => opt.value && opt.label)
        }));
      }

      if (!isErrorInfo(areasRes)) {
        const mappedAreas = (areasRes || []).reduce((acc, area) => {
          const key = (area as any)?.id || (area as any)?.name;
          if (key) acc[String(key)] = (area as any).name;
          return acc;
        }, {} as Record<string, string>);
        setAreaNameMap(mappedAreas);

        setCreateModalLookups((prev) => ({
          ...prev,
          areaOptions: (areasRes || [])
            .map((area: any) => ({
              value: String(area?.id || area?.name || ''),
              label: String(area?.name || '')
            }))
            .filter((opt) => opt.value && opt.label)
        }));
      }

      if (!isErrorInfo(addOnsRes)) {
        const addons = (addOnsRes || [])
          .map((addon: any) => ({
            value: String(addon?.id ?? ''),
            label: String(addon?.name || ''),
            packageId: addon?.packageId !== undefined ? String(addon.packageId) : undefined
          }))
          .filter((opt) => opt.value && opt.label);

        const uniqueById = Array.from(new Map(addons.map((opt) => [opt.value, opt])).values());

        setCreateModalLookups((prev) => ({
          ...prev,
          addonsOptions: uniqueById
        }));
      }
    } catch (error) {
      console.error('Error loading furnishing lookup data:', error);
    } finally {
      setLookupsLoaded(true);
    }
  };

  useEffect(() => {
    loadLookups();
    loadDesignTeamUsers();
  }, []);

  const fetchRequests = async (
    initialOffset?: number,
    initialStatus?: RequestStatus,
    initialLimit?: number
  ) => {
    setIsLoading(true);

    try {
      const response = await getAllFurnishingRequests({
        offset: initialOffset || offset,
        limit: initialLimit || limit,
        status: initialStatus === null ? undefined : initialStatus || undefined
      });
      if (!isErrorInfo(response)) {
        if (response.data) {
          const mappedRequests: FurnishingRequest[] = response.data.map(
            (item: FurnishingRequestResponse) => ({
              id: item.id,
              fullName: item.fullName || `${item.firstName || ''} ${item.lastName || ''}`.trim(),
              email: item.email || '-',
              price: item.price || '-',
              phoneNumber: item.phoneNumber || '-',
              source: (item as any).source,
              typeOfUnit: mapIdToLabel(
                homeTypeMap,
                (item as any).typeOfUnit || (item as any).typeOfUnitId || item.typeOfUnit,
                (item as any).typeOfUnit || item.typeOfUnit || '-'
              ),
              area: mapIdToLabel(areaNameMap, (item as any).areaName, '-'),
              package: mapIdToLabel(
                packageNameMap,
                (item as any).packageId || (item as any).package?.id,
                (item as any).package || item.packageName || '-'
              ),
              model: mapIdToLabel(
                modelNameMap,
                (item as any).modelId || (item as any).model?.id,
                (item as any).model || item.modelName || '-'
              ),
              addOns: Array.isArray((item as any).addOns)
                ? ((item as any).addOns as string[])
                : (item as any).addons
                ? [(item as any).addons]
                : item.addOns || [],
              comments: Array.isArray((item as any).comments)
                ? ((item as any).comments as any[])
                    .map((comment) => {
                      if (typeof comment === 'string') {
                        return { text: comment } as FurnishingRequestComment;
                      }
                      return {
                        text: String((comment as any).text || ''),
                        author: (comment as any).author || 'Unknown',
                        createdAt: (comment as any).createdAt
                      } as FurnishingRequestComment;
                    })
                    .filter((comment) => !!comment.text)
                : [],
              status: ((item.status as RequestStatus) || 'new') as RequestStatus,
              assignedToId: (item as any)?.assignedToId || '',
              assignedToEmail: (item as any)?.assignedToEmail || '',
              assignedToSalesId: (item as any)?.assignedToSalesId || '',
              assignedToSalesEmail: (item as any)?.assignedToSalesEmail || '',
              createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-',
              unitTypeGroup: mapIdToLabel(
                unitTypeGroupMap,
                (item as any).unitTypeGroup || (item as any).unitTypeGroupId,
                '-'
              ),
              ids: {
                typeOfUnit:
                  (item as any).typeOfUnit || (item as any).typeOfUnitId || item.typeOfUnit,
                unitTypeGroup: (item as any).unitTypeGroup || (item as any).unitTypeGroupId,
                areaName: (item as any).areaName,
                packageId: (item as any).packageId || (item as any).package?.id,
                modelId: (item as any).modelId || (item as any).model?.id
              }
            })
          );
          setRequests(mappedRequests);
          setTotalCount(response.count || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching furnishing requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (lookupsLoaded) {
      fetchRequests();
    }
  }, [lookupsLoaded]);

  const allAssigneeOptions = useMemo(
    () => [...assigneeDesignOptions, ...assigneeSalesOptions],
    [assigneeDesignOptions, assigneeSalesOptions]
  );
  const findAssigneeEmail = useCallback(
    (assignedToId?: string) => allAssigneeOptions.find((opt) => opt.id === assignedToId)?.email,
    [allAssigneeOptions]
  );

  const persistRequestUpdate = async (
    requestId: string,
    updates: {
      status?: RequestStatus;
      assignedToId?: string;
      assignedToSalesId?: string;
      comments?: FurnishingRequestComment[];
    }
  ) => {
    const currentRequest = requests.find((req) => req.id === requestId);
    const statusToSend = updates.status ?? currentRequest?.status;

    const commentsToSend = updates.comments ?? currentRequest?.comments;

    const designAssigneeId = updates.assignedToId ?? currentRequest?.assignedToId;
    const designEmail = designAssigneeId ? findAssigneeEmail(designAssigneeId) : undefined;

    const salesAssigneeId = updates.assignedToSalesId ?? currentRequest?.assignedToSalesId;
    const salesEmail = salesAssigneeId ? findAssigneeEmail(salesAssigneeId) : undefined;

    const response = await updateFurnishingRequest(requestId, {
      status: statusToSend,
      assignedToId: designAssigneeId ? designAssigneeId : undefined,
      assignedToEmail: designEmail ? designEmail : undefined,
      assignedToSalesId: salesAssigneeId ? salesAssigneeId : undefined,
      assignedToSalesEmail: salesEmail ? salesEmail : undefined,
      comments: commentsToSend
    } as any);

    if (isErrorInfo(response)) {
      throw new Error(response.message || 'Failed to update furnishing request');
    }
  };

  const handleStatusChange = async (id: string, status: RequestStatus) => {
    const currentRequest = requests.find((req) => req.id === id);

    if (
      (status === 'complete' && !(isDesignTeamUser || isAdminUser)) ||
      ((status === 'won' || status === 'lost') && !(isSalesUser || isAdminUser))
    ) {
      return;
    }

    const isSalesSpecialistUser = isPermited([UserRole.SALES_SPECIALIST]);

    // Sales Manager / Sales Specialist should confirm moving from New -> Ready
    const shouldConfirmNewToReady =
      (isSalesManagerUser || isSalesSpecialistUser) &&
      currentRequest?.status === 'new' &&
      status === 'ready';

    if (shouldConfirmNewToReady) {
      showConfirmDialog(
        'Confirm status change',
        'Once you change the status to “Ready”, the status can be updated only through design team ?',
        async () => {
          const previousRequests = requests;
          setRequests((prev) => prev.map((req) => (req.id === id ? { ...req, status } : req)));

          try {
            await persistRequestUpdate(id, { status });
          } catch (error) {
            console.error('Error updating furnishing request status:', error);
            setRequests(previousRequests);
          }
        },
        'Confirm'
      );
      return;
    }

    const previousRequests = requests;
    setRequests((prev) => prev.map((req) => (req.id === id ? { ...req, status } : req)));

    try {
      await persistRequestUpdate(id, { status });
    } catch (error) {
      console.error('Error updating furnishing request status:', error);
      setRequests(previousRequests);
    }
  };

  const handleDesignAssigneeChange = async (id: string, value: string) => {
    const previousRequests = requests;
    setRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, assignedToId: value } : req))
    );

    try {
      await persistRequestUpdate(id, { assignedToId: value });
      await fetchRequests();
    } catch (error) {
      console.error('Error updating furnishing request design assignee:', error);
      setRequests(previousRequests);
    }
  };

  const handleSalesAssigneeChange = async (id: string, value: string) => {
    const previousRequests = requests;
    setRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, assignedToSalesId: value } : req))
    );

    try {
      await persistRequestUpdate(id, { assignedToSalesId: value });
      await fetchRequests();
    } catch (error) {
      console.error('Error updating furnishing request sales assignee:', error);
      setRequests(previousRequests);
    }
  };

  const handleCommentsChange = async (
    id: string,
    comment: string,
    existingComments: FurnishingRequestComment[]
  ) => {
    const nextComments: FurnishingRequestComment[] = [
      ...existingComments,
      {
        text: comment,
        author: userEmail || 'Unknown',
        createdAt: new Date().toISOString()
      }
    ];

    const previousRequests = requests;
    setRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, comments: nextComments } : req))
    );

    try {
      await persistRequestUpdate(id, { comments: nextComments });
      await fetchRequests();
    } catch (error) {
      console.error('Error updating furnishing request comments:', error);
      setRequests(previousRequests);
    }
  };

  const handlePageChange = (newOffset: number) => {
    setOffset(newOffset);
    fetchRequests(newOffset);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setOffset(1);
    fetchRequests(1, statusFilter as RequestStatus, newLimit);
  };

  const handleStatusFilterChange = (option: any) => {
    setStatusFilter(option?.value || '');
    setOffset(1);

    fetchRequests(1, option?.value || '');
  };

  const handleCreateFurnishingRequest = async (payload: any) => {
    const promise = api.furnishingRequestCreate(payload).then((response) => {
      if (!response.ok || isErrorInfo(response.data)) {
        throw response || new Error('Failed to create furnishing request');
      }
    });

    birdToast.promise(promise, {
      loadingMessage: 'Creating furnishing request',
      successMessage: 'Furnishing request created',
      errorFailedMessage: 'Failed to create furnishing request',
      errorPermissionMessage: 'You do not have permission to create furnishing requests'
    });

    await promise;
  };

  const isDesignTeamUser = isPermited([UserRole.DESIGN_TEAM, UserRole.DESIGN_MANAGER]);
  const isSalesManagerUser = isPermited([UserRole.SALES_MANAGER]);
  const isSalesUser = isPermited([UserRole.SALES_MANAGER, UserRole.SALES_SPECIALIST]);
  const isAdminUser = isPermited([UserRole.ADMIN, UserRole.SUPER_ADMIN]);
  const canAssignDesignTeam = isDesignTeamUser || isAdminUser;
  const canAssignSales = isSalesManagerUser || isAdminUser;
  const canEditRequestDetails = isAdminUser || isSalesUser;

  const sourceOptions = Object.values(FurnishingRequestSource).map((value) => ({
    label: value,
    value
  }));

  return (
    <>
      <div className={styles['furnishing-requests']}>
        <div className={styles['furnishing-requests__header']}>
          <h1 className={styles['furnishing-requests__title']}>Furnishing Requests</h1>

          <div className={styles['furnishing-requests__header-actions']}>
            {!isPermited([UserRole.SALES_SPECIALIST]) && (
              <div className={styles['furnishing-requests__filters']}>
                <Select
                  options={statusOptions}
                  value={statusOptions.find((opt) => opt.value === statusFilter)}
                  onChange={handleStatusFilterChange}
                  placeholder="Filter by status"
                  isClearable
                  className={styles['furnishing-requests__status-filter']}
                />
              </div>
            )}

            {canEditRequestDetails && (
              <div className={styles['furnishing-requests__create-action']}>
                <Button onClick={() => setCreateModalOpen(true)} classNameButton="h-[38px]">
                  Add
                </Button>
              </div>
            )}
          </div>
        </div>

        <Modal
          config={{
            showModal: confirmDialog.show,
            fullScreen: false,
            maxWidth: 'sm',
            showClose: false
          }}
          eventHandlers={{
            closeModalHandler: hideConfirmDialog
          }}
        >
          <div className="p-8 min-w-[400px]">
            <h2 className="m-0 mb-4 text-4xl font-gilroy-medium text-neutral-500 font-semibold">
              {confirmDialog.title}
            </h2>
            <p className="m-0 mb-8 text-2xl text-neutral-500 leading-relaxed">
              {confirmDialog.message}
            </p>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={hideConfirmDialog}
                className="px-7 py-3 rounded-lg font-semibold text-[1.05rem] border border-[rgba(120,120,120,0.4)] text-[#555] bg-transparent hover:bg-[#555] hover:text-white hover:border-[#555] transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="px-7 py-3 rounded-lg font-semibold text-[1.05rem] bg-[#237c58] text-white hover:bg-[#1d6a4b] hover:-translate-y-px hover:shadow transition"
              >
                {confirmDialog.confirmText}
              </button>
            </div>
          </div>
        </Modal>

        {isLoading ? (
          <div className={styles['furnishing-requests__loader']}>
            <img src={`${PUBLIC_PATH}/assets/svgs/loader.svg`} alt="loader" />
          </div>
        ) : (
          <>
            <TableContainer
              component={Paper}
              className={styles['furnishing-requests__table-container']}
            >
              <Table aria-label="furnishing requests table">
                <TableHead>
                  <TableRow className={styles['furnishing-requests__table-head']}>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell>Name</TableCell>
                    {!isPermited([UserRole.DESIGN_TEAM, UserRole.DESIGN_MANAGER]) && (
                      <TableCell>Email</TableCell>
                    )}
                    <TableCell>Price</TableCell>
                    {!isPermited([UserRole.DESIGN_TEAM, UserRole.DESIGN_MANAGER]) && (
                      <TableCell>Phone Number</TableCell>
                    )}
                    <TableCell>Type of Unit</TableCell>
                    <TableCell>No of Bedrooms</TableCell>
                    <TableCell>Package</TableCell>
                    <TableCell>Style</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell className={styles['addons-table-cell']}>AddOns</TableCell>
                    <TableCell>Assigned Design</TableCell>
                    <TableCell>Assigned Sales</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((request) => (
                    <Row
                      key={request.id}
                      request={request}
                      onStatusChange={handleStatusChange}
                      onEditRequest={(req) => {
                        setEditingRequest(req);
                        setEditModalOpen(true);
                      }}
                      onDesignAssigneeChange={handleDesignAssigneeChange}
                      onSalesAssigneeChange={handleSalesAssigneeChange}
                      onCommentsChange={handleCommentsChange}
                      assigneeDesignOptions={assigneeDesignOptions}
                      assigneeSalesOptions={assigneeSalesOptions}
                      canAssignDesignAssignee={canAssignDesignTeam}
                      canAssignSalesAssignee={canAssignSales}
                      canEditRequestDetails={canEditRequestDetails}
                      isDesignTeamUser={isDesignTeamUser}
                      isSalesUser={isSalesUser}
                      isAdminUser={isAdminUser}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {requests.length === 0 && <NotFoundData />}

            {totalCount > 0 && (
              <Pagination
                itemCounts={totalCount}
                itemsPerPage={limit}
                offset={offset}
                handlePageChange={handlePageChange}
                handleItemsPerPage={handleItemsPerPageChange}
              />
            )}
          </>
        )}
      </div>
      {/* Create Modal */}
      <FurnishingRequestFormModal<FurnishingRequestSource>
        config={{
          showModal: createModalOpen,
          title: 'Create Furnishing Request',
          submitLabel: 'Create',
          submittingLabel: 'Creating...'
        }}
        data={{
          ...createModalLookups,
          sourceOptions
        }}
        initialValues={{
          fullName: '',
          email: '',
          phoneNumber: '',
          source: '',
          typeOfUnit: '',
          unitTypeGroup: '',
          areaName: '',
          addons: [],
          packageId: '',
          modelId: '',
          price: ''
        }}
        eventHandlers={{
          closeHandler: (didCreate?: boolean) => {
            setCreateModalOpen(false);
            if (didCreate) fetchRequests(1);
          },
          submitHandler: handleCreateFurnishingRequest
        }}
      />
      {/*  Edit Modal */}
      <FurnishingRequestFormModal<FurnishingRequestSource>
        config={{
          showModal: editModalOpen,
          title: 'Edit Furnishing Request',
          submitLabel: 'Update',
          submittingLabel: 'Updating...'
        }}
        data={{
          ...createModalLookups,
          sourceOptions
        }}
        initialValues={
          editingRequest
            ? {
                fullName: editingRequest.fullName || '',
                email: editingRequest.email === '-' ? '' : editingRequest.email,
                phoneNumber: editingRequest.phoneNumber === '-' ? '' : editingRequest.phoneNumber,
                source: editingRequest.source as FurnishingRequestSource,
                typeOfUnit: String(editingRequest.ids?.typeOfUnit || ''),
                unitTypeGroup: String(editingRequest.ids?.unitTypeGroup || ''),
                areaName: String(editingRequest.ids?.areaName || ''),
                addons: editingRequest.addOns || [],
                packageId: String(editingRequest.ids?.packageId || ''),
                modelId: String(editingRequest.ids?.modelId || ''),
                price: editingRequest.price === '-' ? '' : String(editingRequest.price || '')
              }
            : null
        }
        eventHandlers={{
          closeHandler: (didUpdate?: boolean) => {
            setEditModalOpen(false);
            setEditingRequest(null);
            if (didUpdate) fetchRequests();
          },
          submitHandler: async (payload) => {
            if (!editingRequest) return;

            const toOptionalNumber = (value: unknown) => {
              if (value === null || value === undefined) return undefined;
              const stringValue = String(value).trim();
              if (stringValue === '' || stringValue === 'NaN') return undefined;
              const numberValue = Number(stringValue);
              return Number.isNaN(numberValue) ? undefined : numberValue;
            };

            const data = {
              fullName: payload.fullName,
              email: payload.email,
              phoneNumber: payload.phoneNumber,
              source: payload.source,
              typeOfUnit: payload.typeOfUnit,
              unitTypeGroup: payload.unitTypeGroup,
              areaName: payload.areaName,
              addons: payload.addons,
              packageId: payload.packageId,
              modelId: toOptionalNumber(payload.modelId),
              price: toOptionalNumber(payload.price)
            };

            const response = await updateFurnishingRequest(editingRequest.id, data);

            if (isErrorInfo(response)) {
              throw new Error(response.message || 'Failed to update furnishing request');
            }
            await fetchRequests();
          }
        }}
      />
    </>
  );
};

function mapStateToProps(state: RootState) {
  return {
    isAuthenticated: !!state.auth.accessToken,
    userRole: state.auth.role,
    userRoles: state.auth.roles,
    userEmail: state.auth.email,
    userNotifications: state.auth.userNotifications
  };
}
export default connect(mapStateToProps)(FurnishingRequestsContainer);
